import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { format, parse, isValid } from "date-fns";
import { kk, ru } from "date-fns/locale";
import { useField, useFormikContext } from "formik";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Tooltip } from "react-tooltip";
import "./Calendar.module.css";
import { default as style, default as styles } from "./Calendar.module.css";
import { useTranslation } from "react-i18next";

export interface ICalendarProps {
  name: string;
  disabled?: boolean;
  onChange?: (value?: string) => void;
  documentStatus?:any;
  outputFormat?: "dd.MM.yyyy" | "yyyy-MM";
  allowFuture?: boolean;
  // Показывать выпадающие списки месяца/года в шапке
  showMonthYearSelect?: boolean;
  // Кастомный набор лет для селекта
  yearOptions?: number[];
  // Принудительно показывать сетку дней даже при формате "yyyy-MM"
  showDayGridForMonthYear?: boolean;
  // Плейсхолдер поля ввода
  placeholder?: string;
}

export const CalendarSelectFormik: React.FC<ICalendarProps> = (props) => {
  const { documentStatus } = props;
  const { t, i18n } = useTranslation();
  const { setFieldValue, validateField } = useFormikContext();
  const [field, meta] = useField(props.name);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const tooltipContentRef = useRef<HTMLDivElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    if (!field.value) return undefined;
    let parsed = parse(field.value, "dd.MM.yyyy", new Date());
    if (isValid(parsed)) return parsed;
    parsed = parse(field.value, "yyyy-MM", new Date());
    if (isValid(parsed)) return parsed;
    parsed = parse(field.value, "yyyy-MM-dd", new Date());
    if (isValid(parsed)) return parsed;
    const asDate = new Date(field.value);
    return isValid(asDate) ? asDate : undefined;
  });

  const current = new Date();

  const months = i18n.language?.includes("ru")
    ? ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"]
    : ["Қаңтар","Ақпан","Наурыз","Сәуір","Мамыр","Маусым","Шілде","Тамыз","Қыркүйек","Қазан","Қараша","Желтоқсан"];

  const defaultYears = (() => {
    const start = 1950;
    const end = props.allowFuture ? current.getFullYear() + 10 : current.getFullYear();
    const arr: number[] = [];
    for (let y = start; y <= end; y++) arr.push(y);
    return arr;
  })();
  const years = props.yearOptions && props.yearOptions.length ? props.yearOptions : defaultYears;

  const initialMonth = (selectedDate ?? current).getMonth();
  const initialYear = (selectedDate ?? current).getFullYear();
  const [monthIdx, setMonthIdx] = useState<number>(initialMonth);
  const [yearVal, setYearVal] = useState<number>(initialYear);
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date(initialYear, initialMonth, 1));
   // Значение селекта месяцев. Держим отдельно, чтобы можно было сбрасывать на плейсхолдер
   const [monthSelectValue, setMonthSelectValue] = useState<number | "">(initialMonth);

  const handleArrowClick = () => {
    setIsTooltipOpen(!isTooltipOpen);
  };

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      // синхронизируем выбранный месяц в гриде и хедере
      setMonthIdx(date.getMonth());
      setYearVal(date.getFullYear());
      setDisplayMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      const formattedDate = format(date, props.outputFormat ?? "dd.MM.yyyy");
      setFieldValue(props.name, formattedDate, false); // Сохраняем дату в нужном формате
      validateField(props.name); // Обновляем валидацию только для этого поля
      if (props.onChange) {
        props.onChange(formattedDate);
      }
      // закрываем тултип после выбора даты
      setIsTooltipOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedDate(undefined);
    setFieldValue(props.name, "", true); // Триггерим валидацию при очистке
    if (props.onChange) {
      props.onChange("");
    }
    setIsTooltipOpen(false);
  };

  useEffect(() => {
    if (!field.value) {
      setSelectedDate(undefined);
      return;
    }
    let parsedDate = parse(field.value, "dd.MM.yyyy", new Date());
    if (isNaN(parsedDate.getTime())) {
      parsedDate = parse(field.value, "yyyy-MM", new Date());
    }
    if (!isNaN(parsedDate.getTime())) {
      setSelectedDate(parsedDate);
      setDisplayMonth(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
      setMonthIdx(parsedDate.getMonth());
      setYearVal(parsedDate.getFullYear());
    } else {
      console.error("Invalid parsed date:", field.value);
    }
  }, [field.value]);

    // При открытии тултипа сбрасываем значение селекта месяцев,
    // чтобы пользователь мог выбрать тот же месяц (включая январь)
    useEffect(() => {
      if (isTooltipOpen) {
        setMonthSelectValue("");
      }
    }, [isTooltipOpen]);

    // Закрытие по клику вне тултипа/контрола
    useEffect(() => {
      const onDocMouseDown = (e: MouseEvent) => {
        if (!isTooltipOpen) return;
        const target = e.target as Node;
        if (rootRef.current?.contains(target)) return;
        if (tooltipContentRef.current?.contains(target)) return;
        setIsTooltipOpen(false);
      };
      document.addEventListener("mousedown", onDocMouseDown, true);
      return () => document.removeEventListener("mousedown", onDocMouseDown, true);
    }, [isTooltipOpen]);

  const isMonthDisabled = (mIdx: number, y: number) => {
    if (props.allowFuture) return false;
    if (y > current.getFullYear()) return true;
    if (y < current.getFullYear()) return false;
    return mIdx > current.getMonth();
  };

  const handleApplyMonthYear = (mIdx: number, y: number, apply: boolean) => {
    const candidate = new Date(y, mIdx, 1);
    if (!props.allowFuture) {
      const nowStart = new Date(current.getFullYear(), current.getMonth(), 1);
      if (candidate > nowStart) return;
    }
    setDisplayMonth(candidate);
    // Если нужен формат месяца — применяем сразу
    if ((props.outputFormat ?? "dd.MM.yyyy") === "yyyy-MM" && apply) {
      handleDateChange(candidate);
    }
  };

  const renderMonthYearHeader = () => {
    const showHeader = props.showMonthYearSelect || props.outputFormat === "yyyy-MM";
    if (!showHeader) return null;
    return (
      <div className={styles.monthYearRow}>
         <select
           value={monthSelectValue as any}
          onChange={(e) => {
            const m = Number(e.target.value);
            setMonthIdx(m);
             setMonthSelectValue(m);
            // смена месяца — считаем финальным выбором для формата yyyy-MM
            handleApplyMonthYear(m, yearVal, true);
          }}
          className={styles.select}
        >
           <option value="" disabled hidden>
             {t("components.choose")}
           </option>
          {months.map((label, idx) => (
            <option key={idx} value={idx} disabled={isMonthDisabled(idx, yearVal)}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={yearVal}
          onChange={(e) => {
            const y = Number(e.target.value);
            setYearVal(y);
            // смена года — лишь обновляем видимый месяц/год, без применения значения
            handleApplyMonthYear(monthIdx, y, false);
          }}
          className={styles.select}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderCalendar = () => {
    const showHeader = props.showMonthYearSelect || (props.outputFormat === "yyyy-MM");
    const headerOnly = (props.outputFormat === "yyyy-MM") && !props.showDayGridForMonthYear;

    if (headerOnly) {
      return renderMonthYearHeader();
    }
    return (
      <div>
        {showHeader ? renderMonthYearHeader() : null}
        <DayPicker
          locale={i18n.language?.includes("ru") ? ru : kk}
          id={`calendar-select-${props.name}`}
          selected={selectedDate}
          onSelect={handleDateChange}
          mode="single"
          month={displayMonth}
          onMonthChange={(m) => {
            if (m) {
              setDisplayMonth(m);
              setMonthIdx(m.getMonth());
              setYearVal(m.getFullYear());
            }
          }}
          className={styles.rdp}
          disabled={props.allowFuture ? undefined : { after: new Date() }}
        />
      </div>
    );
  };

  return (
     <div className={styles.inputContainer} ref={rootRef}>
      <input
        type="text"
        value={selectedDate && isValid(selectedDate) ? format(selectedDate, props.outputFormat ?? "dd.MM.yyyy") : ""}
        placeholder={props.placeholder ?? t("components.choose")}
        readOnly
        disabled={props.disabled}
        className={styles.field}
        data-tooltip-id={`select-date-tooltip-${props.name}`}
        onClick={handleArrowClick}
      />
      <div className={styles.rightSlot}>
        {(documentStatus && (documentStatus === "DRAFT" || documentStatus === "new")) ? (
          field.value || selectedDate ? (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
            >
              &times;
            </button>
          ) : (
            <img
              className={styles.arrow}
              src="/icons/calendar.svg"
              onClick={handleArrowClick}
              data-tooltip-id={`select-date-tooltip-${props.name}`}
            />
          )
        ) : null}
      </div>
      <Tooltip
        id={`select-date-tooltip-${props.name}`}
        className={classNames(style.wrapperTooltip, "drop-shadow-2xl")}
        isOpen={isTooltipOpen}
        openOnClick={true}
        clickable={true}
        noArrow={true}
        opacity={1}
        place={"bottom-end"}
        style={{ zIndex: 10050, position: "fixed" }}
        afterShow={() => setIsTooltipOpen(true)}
        afterHide={() => setIsTooltipOpen(false)}
      >
         <div ref={tooltipContentRef}>
           {renderCalendar()}
         </div>
      </Tooltip>
    </div>
  );
};

export default CalendarSelectFormik;
